// src/pages/dashboard/CreateBranchModal.tsx
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTrash2, FiUpload, FiX } from 'react-icons/fi';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import { Branch } from '../../../utils/supabaseClient';
import { uploadToSupabaseStorage } from '../../../utils/supabaseStorage';
import { createNewBranch } from '../../../store/slices/branchSlice';
import { useAppDispatch } from '../../../store/hooks';
import { defaultOperatingHours, defaultTheme } from '../../../utils/defaultValues';

interface CreateBranchModalProps {
	organizationId: string;
	onClose: () => void;
	onBranchCreated: (branch?: Branch) => void;
}

const CreateBranchModal: React.FC<CreateBranchModalProps> = ({
	organizationId,
	onClose,
	onBranchCreated
}) => {
	const { t } = useTranslation();
	const [branchData, setBranchData] = useState({
		name: '',
		description: ''
	});
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [logoPreview, setLogoPreview] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);

	const dispatch = useAppDispatch()

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setBranchData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];

			// Validate file type
			const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
			if (!validTypes.includes(file.type)) {
				setError(t('branches.errors.invalidFileType'));
				return;
			}

			// Validate file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				setError(t('branches.errors.fileTooLarge'));
				return;
			}

			setLogoFile(file);
			setError(null);

			// Create a preview
			const reader = new FileReader();
			reader.onload = () => {
				setLogoPreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const removeLogo = () => {
		setLogoFile(null);
		setLogoPreview(null);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!branchData.name.trim()) {
			setError(t('branches.errors.nameRequired'));
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			let logoUrl = null;

			// Upload logo if provided
			if (logoFile) {
				setUploadProgress(0);

				const fileName = `logo-${Date.now()}.${logoFile.name.split('.').pop()}`;
				const result = await uploadToSupabaseStorage(logoFile, fileName, {
					bucket: 'uploads',
					path: `logos/${organizationId}`,
					upsert: true
				});

				logoUrl = result.publicUrl;
				setUploadProgress(100);
			}

			const payload = {
				organization_id: organizationId!,
				name: branchData.name,
				description: branchData.description || null,
				logo: logoUrl,
				theme: defaultTheme as any,
				operating_hours: defaultOperatingHours as any,
				is_active: false
			}
			// const result = await insertBranch(payload);
			// if (!result) return;
			// if (!result.success) console.log(result.message);

			const createdBranch = await dispatch(createNewBranch(payload)).unwrap()

			// Clear progress after success
			setTimeout(() => {
				setUploadProgress(undefined);
			}, 1000);

			onBranchCreated(createdBranch);
		} catch (err) {
			console.error('Error creating branch:', err);
			setError(t('branches.errors.createFailed'));
			setUploadProgress(undefined);
		} finally {
			setIsSubmitting(false);
		}
	};

	const modalVariants = {
		hidden: { opacity: 0, scale: 0.9 },
		visible: {
			opacity: 1,
			scale: 1,
			transition: { duration: 0.3 }
		},
		exit: {
			opacity: 0,
			scale: 0.9,
			transition: { duration: 0.2 }
		}
	};

	return (
		<div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
			<motion.div
				variants={modalVariants}
				initial="hidden"
				animate="visible"
				exit="exit"
				className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden"
			>
				<div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<h2 className="text-xl font-bold text-gray-900 dark:text-white">
						{t('branches.createNew')}
					</h2>
					<button
						onClick={onClose}
						className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
					>
						<FiX size={24} />
					</button>
				</div>

				<div className="p-6">
					{error && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4"
						>
							{error}
						</motion.div>
					)}

					<form onSubmit={handleSubmit}>
						<div className="space-y-6">
							{/* Branch Name */}
							<InputField
								label={t('branches.name')}
								name="name"
								value={branchData.name}
								onChange={handleChange}
								placeholder={t('branches.namePlaceholder')}
								required
								autoFocus
							/>

							{/* Description */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									{t('branches.description')}
								</label>
								<textarea
									name="description"
									value={branchData.description}
									onChange={handleChange}
									placeholder={t('branches.descriptionPlaceholder')}
									className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500"
									rows={3}
								/>
							</div>

							{/* Logo Upload */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									{t('branches.logo')} <span className="text-sm text-gray-500">({t('branches.optional')})</span>
								</label>

								{logoPreview ? (
									<div className="relative">
										<div className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
											<img
												src={logoPreview}
												alt="Logo preview"
												className="w-16 h-16 object-contain rounded-lg"
											/>
											<div className="flex-1">
												<p className="text-sm font-medium text-gray-900 dark:text-white">
													{t('branches.logoSelected')}
												</p>
												<p className="text-xs text-gray-500 dark:text-gray-400">
													{logoFile?.name}
												</p>
											</div>
											<button
												type="button"
												onClick={removeLogo}
												className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer"
											>
												<FiTrash2 className="h-4 w-4" />
											</button>
										</div>

										{/* Progress bar during upload */}
										{uploadProgress !== undefined && uploadProgress < 100 && (
											<div className="mt-2">
												<div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
													<div
														className="bg-primary h-2 rounded-full transition-all duration-300"
														style={{ width: `${uploadProgress}%` }}
													/>
												</div>
												<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
													{t('branches.uploading')} {uploadProgress}%
												</p>
											</div>
										)}
									</div>
								) : (
									<div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary cursor-pointer transition-colors">
										<input
											type="file"
											accept="image/*"
											onChange={handleLogoChange}
											className="hidden"
											id="logo-upload"
										/>
										<label htmlFor="logo-upload" className="cursor-pointer">
											<FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
											<p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
												{t('branches.uploadLogo')}
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400">
												{t('branches.supportedFormats')}
											</p>
										</label>
									</div>
								)}
							</div>
						</div>

						<div className="mt-6 flex justify-end gap-3">
							<Button
								variant="outline"
								onClick={onClose}
								disabled={isSubmitting}
							>
								{t('common.cancel')}
							</Button>
							<Button
								type="submit"
								isLoading={isSubmitting}
								disabled={!branchData.name.trim()}
							>
								{t('branches.create')}
							</Button>
						</div>
					</form>
				</div>
			</motion.div>
		</div>
	);
};

export default CreateBranchModal;